(() => {
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const MIN_PATH_LENGTH = 48;
  const ARROWS_PER_PATH = 3;
  const ARROW_POINTS = '12,0 -7,-6.5 -7,6.5';

  const initHeroVideo = (section) => {
    const openButton = section.querySelector('[data-elevent-video-open]');
    const modal = section.querySelector('[data-elevent-video-modal]');
    const closeButton = section.querySelector('[data-elevent-video-close]');
    const frame = section.querySelector('[data-elevent-video-frame]');

    if (!openButton || !modal || !frame) return;

    const embedSrc = frame.dataset.src || '';

    const closeModal = () => {
      modal.close();
      frame.removeAttribute('src');
    };

    openButton.addEventListener('click', () => {
      if (embedSrc) frame.setAttribute('src', embedSrc);
      if (typeof modal.showModal === 'function') modal.showModal();
    });

    if (closeButton) closeButton.addEventListener('click', closeModal);

    modal.addEventListener('click', (event) => {
      if (event.target === modal) closeModal();
    });

    modal.addEventListener('close', () => {
      frame.removeAttribute('src');
    });
  };

  const parseColorChannels = (value) => {
    if (!value || value === 'none' || value === 'transparent') return null;

    const normalized = String(value).trim().toLowerCase();

    if (normalized.startsWith('#')) {
      let hex = normalized.slice(1);
      if (hex.length === 3) {
        hex = hex
          .split('')
          .map((char) => char + char)
          .join('');
      }
      if (hex.length !== 6) return null;
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }

    const rgbMatch = normalized.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/);
    if (rgbMatch) {
      return {
        r: Number(rgbMatch[1]),
        g: Number(rgbMatch[2]),
        b: Number(rgbMatch[3]),
      };
    }

    return null;
  };

  const isGreenish = (color) => {
    const channels = parseColorChannels(color);
    if (!channels) return false;
    const { r, g, b } = channels;
    return g > 90 && g > r * 1.15 && g > b * 1.1;
  };

  const isDarkRouteColor = (color) => {
    const channels = parseColorChannels(color);
    if (!channels) return false;
    const { r, g, b } = channels;
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance < 90;
  };

  const readPaint = (el, attrName) => {
    const direct = el.getAttribute(attrName);
    if (direct && direct !== 'inherit') return direct;

    const styleAttr = el.getAttribute('style') || '';
    const styleMatch = styleAttr.match(new RegExp(`${attrName}\\s*:\\s*([^;]+)`, 'i'));
    if (styleMatch) return styleMatch[1].trim();

    let node = el.parentElement;
    while (node && node.tagName && node.tagName.toLowerCase() !== 'svg') {
      const inherited = node.getAttribute(attrName);
      if (inherited && inherited !== 'inherit') return inherited;
      node = node.parentElement;
    }

    return direct || '';
  };

  const isAnimatablePath = (pathEl) => {
    let length = 0;
    try {
      length = pathEl.getTotalLength();
    } catch (error) {
      return false;
    }

    if (!Number.isFinite(length) || length < MIN_PATH_LENGTH) return false;

    const fill = readPaint(pathEl, 'fill');
    const stroke = readPaint(pathEl, 'stroke');
    const strokeWidth = Number.parseFloat(pathEl.getAttribute('stroke-width') || '1');

    // Keep thin black route strokes; skip green decorative fills/blobs.
    if (isGreenish(fill) && (!stroke || stroke === 'none' || isGreenish(stroke))) {
      return false;
    }

    if (isGreenish(stroke) && strokeWidth >= 4) return false;

    if (stroke && stroke !== 'none') {
      if (isDarkRouteColor(stroke) || !isGreenish(stroke)) return true;
      return false;
    }

    // Stroke-less paths: only animate dark filled ribbons that are long enough.
    if (fill && fill !== 'none' && isDarkRouteColor(fill) && !isGreenish(fill)) {
      return true;
    }

    // Unknown paint — still usable if the geometry looks like a route.
    return length >= 120;
  };

  const extractSvgMarkup = (rawText) => {
    if (!rawText) return null;
    const trimmed = rawText.trim();
    if (!trimmed.includes('<svg')) return null;

    const doc = new DOMParser().parseFromString(trimmed, 'image/svg+xml');
    const parsedSvg = doc.querySelector('svg');
    if (!parsedSvg || doc.querySelector('parsererror')) return null;
    return parsedSvg;
  };

  const normalizeInlineSvg = (svg) => {
    svg.removeAttribute('width');
    svg.removeAttribute('height');
    svg.setAttribute('preserveAspectRatio', svg.getAttribute('preserveAspectRatio') || 'xMidYMax meet');
    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.style.display = 'block';
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    return svg;
  };

  const createArrowMarker = () => {
    const arrow = document.createElementNS(SVG_NS, 'polygon');
    arrow.setAttribute('points', ARROW_POINTS);
    arrow.setAttribute('class', 'elevent-hero__path-arrow');
    return arrow;
  };

  const readArrowSizeScale = (section) => {
    const raw = Number.parseFloat(section.dataset.pathArrowsSize || '100');
    const percent = Number.isFinite(raw) ? raw : 100;
    // Soft floor 40% keeps arrows readable; 100% = current designed size.
    return Math.max(0.4, Math.min(1, percent / 100));
  };

  // Outflow-only: denser markers, scaled relative to shared path_arrows_size.
  const readOutArrowCount = (section) => {
    const raw = Number.parseInt(section.dataset.pathArrowsOutCount || '6', 10);
    if (!Number.isFinite(raw)) return 6;
    return Math.max(3, Math.min(8, raw));
  };

  const readOutArrowSizeScale = (section, baseScale) => {
    const raw = Number.parseFloat(section.dataset.pathArrowsOutSize || '60');
    const percent = Number.isFinite(raw) ? raw : 60;
    // Soft floor 30% of shared size so outflow can read smaller than back/front.
    const relative = Math.max(0.3, Math.min(1, percent / 100));
    return baseScale * relative;
  };

  const readStrokeSettings = (section) => {
    const hideStrokes = section.dataset.pathsHideStrokes === 'true';
    const rawScale = Number.parseFloat(section.dataset.pathsStrokeScale || '100');
    const strokeScale = Number.isFinite(rawScale) ? Math.max(0, Math.min(100, rawScale)) : 100;
    return { hideStrokes, strokeScale };
  };

  const applyRouteStrokeStyles = (pathEl, { hideStrokes, strokeScale }) => {
    pathEl.classList.add('elevent-hero__route');

    if (pathEl.dataset.originalStrokeWidth === undefined) {
      const attrWidth = pathEl.getAttribute('stroke-width');
      if (attrWidth != null && attrWidth !== '') {
        pathEl.dataset.originalStrokeWidth = attrWidth;
      } else {
        const styleAttr = pathEl.getAttribute('style') || '';
        const styleMatch = styleAttr.match(/stroke-width\s*:\s*([^;]+)/i);
        pathEl.dataset.originalStrokeWidth = styleMatch ? styleMatch[1].trim() : '1';
      }
    }

    const hideVisually = hideStrokes || strokeScale <= 0;
    pathEl.classList.toggle('is-stroke-hidden', hideVisually);

    if (hideVisually) return;

    const originalWidth = Number.parseFloat(pathEl.dataset.originalStrokeWidth);
    if (!Number.isFinite(originalWidth)) return;

    if (strokeScale >= 99.5) {
      pathEl.setAttribute('stroke-width', pathEl.dataset.originalStrokeWidth);
    } else {
      const scaled = Math.max(0, originalWidth * (strokeScale / 100));
      pathEl.setAttribute('stroke-width', String(scaled));
    }
  };

  // Soft edge fades along each route (fraction of path length).
  const ARROW_FADE_ZONE = 0.22;
  const ARROW_BASE_OPACITY = 0.92;
  let pathFadeMaskSeq = 0;

  const arrowOpacityAlongPath = (layerKind, t) => {
    // t = 0 at path start, 1 at path end.
    let opacity = 1;

    // Back-plate + outflow: fade out near the start.
    if (layerKind === 'back' || layerKind === 'out') {
      if (t <= ARROW_FADE_ZONE) {
        opacity = Math.min(opacity, Math.max(0, t / ARROW_FADE_ZONE));
      }
    }

    // Front (middle) + back-plate + outflow: fade out near the end.
    if (layerKind === 'front' || layerKind === 'back' || layerKind === 'out') {
      if (t >= 1 - ARROW_FADE_ZONE) {
        opacity = Math.min(opacity, Math.max(0, (1 - t) / ARROW_FADE_ZONE));
      }
    }

    return opacity;
  };

  const ensureSvgDefs = (svg) => {
    let defs = svg.querySelector('defs');
    if (!defs) {
      defs = document.createElementNS(SVG_NS, 'defs');
      svg.insertBefore(defs, svg.firstChild);
    }
    return defs;
  };

  const applyPathEdgeFade = (pathEl, layerKind) => {
    const svg = pathEl.ownerSVGElement;
    if (!svg) return;

    let length = 0;
    try {
      length = pathEl.getTotalLength();
    } catch (error) {
      return;
    }
    if (!Number.isFinite(length) || length < MIN_PATH_LENGTH) return;

    const fadeStart = layerKind === 'back' || layerKind === 'out';
    const fadeEnd = layerKind === 'front' || layerKind === 'back' || layerKind === 'out';
    if (!fadeStart && !fadeEnd) return;

    const start = pathEl.getPointAtLength(0);
    const end = pathEl.getPointAtLength(length);
    const defs = ensureSvgDefs(svg);
    pathFadeMaskSeq += 1;
    const gradId = `elevent-path-fade-grad-${pathFadeMaskSeq}`;
    const maskId = `elevent-path-fade-mask-${pathFadeMaskSeq}`;

    const gradient = document.createElementNS(SVG_NS, 'linearGradient');
    gradient.setAttribute('id', gradId);
    gradient.setAttribute('gradientUnits', 'userSpaceOnUse');
    gradient.setAttribute('x1', String(start.x));
    gradient.setAttribute('y1', String(start.y));
    gradient.setAttribute('x2', String(end.x));
    gradient.setAttribute('y2', String(end.y));

    const addStop = (offset, color) => {
      const stop = document.createElementNS(SVG_NS, 'stop');
      stop.setAttribute('offset', offset);
      stop.setAttribute('stop-color', color);
      gradient.appendChild(stop);
    };

    if (fadeStart) {
      addStop('0%', '#000000');
      addStop(`${Math.round(ARROW_FADE_ZONE * 100)}%`, '#ffffff');
    } else {
      addStop('0%', '#ffffff');
    }

    if (fadeEnd) {
      addStop(`${Math.round((1 - ARROW_FADE_ZONE) * 100)}%`, '#ffffff');
      addStop('100%', '#000000');
    } else {
      addStop('100%', '#ffffff');
    }

    const mask = document.createElementNS(SVG_NS, 'mask');
    mask.setAttribute('id', maskId);
    mask.setAttribute('maskUnits', 'userSpaceOnUse');

    const maskRect = document.createElementNS(SVG_NS, 'rect');
    let bbox = null;
    try {
      bbox = pathEl.getBBox();
    } catch (error) {
      bbox = null;
    }
    const pad = 24;
    if (bbox && Number.isFinite(bbox.width) && Number.isFinite(bbox.height)) {
      maskRect.setAttribute('x', String(bbox.x - pad));
      maskRect.setAttribute('y', String(bbox.y - pad));
      maskRect.setAttribute('width', String(Math.max(1, bbox.width + pad * 2)));
      maskRect.setAttribute('height', String(Math.max(1, bbox.height + pad * 2)));
    } else {
      maskRect.setAttribute('x', '0');
      maskRect.setAttribute('y', '0');
      maskRect.setAttribute('width', '100%');
      maskRect.setAttribute('height', '100%');
    }
    maskRect.setAttribute('fill', `url(#${gradId})`);
    mask.appendChild(maskRect);

    defs.appendChild(gradient);
    defs.appendChild(mask);
    pathEl.setAttribute('mask', `url(#${maskId})`);
  };

  const buildRouteTrack = (pathEl, durationMs, pathIndex, arrowScale, layerKind, arrowsPerPath) => {
    let length = 0;
    try {
      length = pathEl.getTotalLength();
    } catch (error) {
      return null;
    }

    if (!Number.isFinite(length) || length < MIN_PATH_LENGTH) return null;

    const svg = pathEl.ownerSVGElement;
    if (!svg) return null;

    let layer = svg.querySelector('[data-elevent-path-arrows]');
    if (!layer) {
      layer = document.createElementNS(SVG_NS, 'g');
      layer.setAttribute('data-elevent-path-arrows', '');
      svg.appendChild(layer);
    }

    const count = Math.max(1, arrowsPerPath || ARROWS_PER_PATH);
    const arrows = [];
    for (let i = 0; i < count; i += 1) {
      const arrow = createArrowMarker();
      layer.appendChild(arrow);
      // Even spacing along the loop; denser when count is higher (outflow).
      // Same pathIndex phase offset keeps layers feeling temporally linked.
      arrows.push({
        el: arrow,
        offset: (i / count + (pathIndex % 3) * 0.07) % 1,
      });
    }

    return {
      path: pathEl,
      length,
      durationMs,
      arrowScale,
      layerKind: layerKind || 'front',
      arrows,
    };
  };

  const placeArrow = (track, arrow, progress) => {
    const t = (progress + arrow.offset) % 1;
    const distance = t * track.length;
    const point = track.path.getPointAtLength(distance);
    const delta = Math.max(1, Math.min(8, track.length * 0.01));
    const ahead = track.path.getPointAtLength(Math.min(track.length, distance + delta));
    const angle = (Math.atan2(ahead.y - point.y, ahead.x - point.x) * 180) / Math.PI;
    const scale = track.arrowScale || 1;
    const opacity = ARROW_BASE_OPACITY * arrowOpacityAlongPath(track.layerKind, t);
    arrow.el.setAttribute(
      'transform',
      `translate(${point.x} ${point.y}) rotate(${angle}) scale(${scale})`
    );
    arrow.el.style.opacity = String(opacity);
  };

  const fetchAndInlineSvg = async (layer) => {
    if (layer.querySelector('svg[data-elevent-inlined]')) {
      return layer.querySelector('svg[data-elevent-inlined]');
    }

    const src = layer.dataset.svgSrc || layer.querySelector('img')?.currentSrc || layer.querySelector('img')?.src;
    if (!src) return null;

    try {
      const response = await fetch(src, { mode: 'cors', credentials: 'omit' });
      if (!response.ok) throw new Error(`SVG fetch failed (${response.status})`);
      const text = await response.text();
      const parsed = extractSvgMarkup(text);
      if (!parsed) throw new Error('Fetched file is not a valid SVG');

      const svg = normalizeInlineSvg(parsed.cloneNode(true));
      svg.setAttribute('data-elevent-inlined', 'true');

      const img = layer.querySelector('img');
      if (img) img.remove();
      layer.appendChild(svg);
      return svg;
    } catch (error) {
      console.warn('[elevent-hero] Could not inline path SVG for animation:', error);
      return null;
    }
  };

  const initPathArrows = (section) => {
    const layers = [...section.querySelectorAll('[data-elevent-paths]')];
    if (!layers.length) return;

    const arrowsEnabled = section.dataset.pathArrowsEnabled !== 'false';
    const strokeSettings = readStrokeSettings(section);
    const arrowScale = readArrowSizeScale(section);
    const outArrowCount = readOutArrowCount(section);
    const outArrowScale = readOutArrowSizeScale(section, arrowScale);
    const needsStrokeStyling = strokeSettings.hideStrokes || strokeSettings.strokeScale < 100;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!arrowsEnabled && !needsStrokeStyling) return;

    section.classList.toggle('is-paths-hidden', strokeSettings.hideStrokes || strokeSettings.strokeScale <= 0);

    // path_arrows_speed is full loop duration in seconds (lower = faster). Soft floor 2s.
    // Shared across back/front/out so all layers move together.
    const durationSec = Number.parseFloat(section.dataset.pathArrowsSpeed || '4');
    const durationMs = Math.max(2000, (Number.isFinite(durationSec) ? durationSec : 4) * 1000);
    const shouldAnimate = arrowsEnabled && !reduceMotion;

    const controller = {
      tracks: [],
      rafId: null,
      visible: false,
      startedAt: 0,
      stopped: false,
    };

    section._eleventPathArrows = controller;

    const tick = (now) => {
      if (controller.stopped || !controller.visible) {
        controller.rafId = null;
        return;
      }

      if (!controller.startedAt) controller.startedAt = now;
      const elapsed = now - controller.startedAt;

      controller.tracks.forEach((track) => {
        const progress = (elapsed % track.durationMs) / track.durationMs;
        track.arrows.forEach((arrow) => placeArrow(track, arrow, progress));
      });

      controller.rafId = requestAnimationFrame(tick);
    };

    const start = () => {
      if (controller.stopped || controller.rafId || !controller.tracks.length) return;
      controller.visible = true;
      controller.startedAt = 0;
      controller.rafId = requestAnimationFrame(tick);
    };

    const stop = () => {
      controller.visible = false;
      if (controller.rafId) {
        cancelAnimationFrame(controller.rafId);
        controller.rafId = null;
      }
    };

    const discoverRoutePaths = (svg) => {
      const paths = [...svg.querySelectorAll('path')];
      if (!paths.length) {
        console.warn('[elevent-hero] Path SVG has no <path> elements to animate.');
        return [];
      }

      const routePaths = paths.filter(isAnimatablePath);
      const targets = routePaths.length
        ? routePaths
        : paths.filter((pathEl) => {
            try {
              return pathEl.getTotalLength() >= MIN_PATH_LENGTH;
            } catch (error) {
              return false;
            }
          });

      if (!targets.length) {
        console.warn('[elevent-hero] No suitable path geometry found for arrows.');
      }

      return targets;
    };

    const setup = async () => {
      const allTracks = [];

      for (let layerIndex = 0; layerIndex < layers.length; layerIndex += 1) {
        const layer = layers[layerIndex];
        const svg = await fetchAndInlineSvg(layer);
        if (!svg) continue;

        const targets = discoverRoutePaths(svg);
        const layerKind = layer.dataset.eleventPaths || 'front';

        targets.forEach((pathEl, pathIndex) => {
          applyRouteStrokeStyles(pathEl, strokeSettings);
          applyPathEdgeFade(pathEl, layerKind);

          if (!shouldAnimate) return;

          const isOut = layerKind === 'out';
          const track = buildRouteTrack(
            pathEl,
            durationMs,
            pathIndex + layerIndex * 7,
            isOut ? outArrowScale : arrowScale,
            layerKind,
            isOut ? outArrowCount : ARROWS_PER_PATH
          );
          if (track) allTracks.push(track);
        });
      }

      controller.tracks = allTracks;
      if (!shouldAnimate || !allTracks.length) return;

      // Seed initial positions before the first frame.
      allTracks.forEach((track) => {
        track.arrows.forEach((arrow) => placeArrow(track, arrow, 0));
      });

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) start();
            else stop();
          });
        },
        { rootMargin: '80px 0px', threshold: 0.05 }
      );

      observer.observe(section);
      controller.observer = observer;

      if (section.getBoundingClientRect().top < window.innerHeight && section.getBoundingClientRect().bottom > 0) {
        start();
      }
    };

    setup();
  };

  const destroyPathArrows = (section) => {
    const controller = section._eleventPathArrows;
    if (!controller) return;
    controller.stopped = true;
    controller.visible = false;
    if (controller.rafId) cancelAnimationFrame(controller.rafId);
    if (controller.observer) controller.observer.disconnect();
    section._eleventPathArrows = null;
  };

  const destroyGlowWind = (section) => {
    if (section._eleventGlowWindObserver) {
      section._eleventGlowWindObserver.disconnect();
      section._eleventGlowWindObserver = null;
    }
    section.classList.remove('is-glow-wind-paused');
  };

  const initGlowWind = (section) => {
    destroyGlowWind(section);

    if (section.dataset.glowWindEnabled === 'false') return;
    if (!section.querySelector('.elevent-hero__glow-wind')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const setPaused = (paused) => {
      section.classList.toggle('is-glow-wind-paused', paused);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setPaused(!entry.isIntersecting);
        });
      },
      { rootMargin: '80px 0px', threshold: 0.05 }
    );

    observer.observe(section);
    section._eleventGlowWindObserver = observer;

    const rect = section.getBoundingClientRect();
    setPaused(!(rect.top < window.innerHeight && rect.bottom > 0));
  };

  const initHeroSection = (section) => {
    destroyPathArrows(section);
    destroyGlowWind(section);
    initHeroVideo(section);
    initPathArrows(section);
    initGlowWind(section);

    const flow = section.querySelector('.elevent-hero__flow');
    const backgroundLayer = section.querySelector('.elevent-hero__background img');
    const parallaxEnabled = section.dataset.parallaxEnabled === 'true';
    if (flow && parallaxEnabled) {
      let rafId = null;

      const onMouseMove = (event) => {
        if (window.matchMedia('(max-width: 989px)').matches) return;

        const rect = section.getBoundingClientRect();
        const offsetX = (event.clientX - rect.left) / rect.width - 0.5;
        const offsetY = (event.clientY - rect.top) / rect.height - 0.5;

        const tx = offsetX * 18;
        const ty = offsetY * 10;

        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          flow.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
        });
      };

      const onMouseLeave = () => {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          flow.style.transform = 'translate3d(0, 0, 0)';
        });
      };

      section.addEventListener('mousemove', onMouseMove);
      section.addEventListener('mouseleave', onMouseLeave);
    }

    if (backgroundLayer) {
      let scrollRafId = null;

      const updateBackgroundParallax = () => {
        scrollRafId = null;

        const rect = section.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const progress = (viewportHeight - rect.top) / (viewportHeight + rect.height);
        const clamped = Math.max(0, Math.min(1, progress));
        const shiftRange = window.matchMedia('(max-width: 989px)').matches ? 56 : 120;
        const translateY = (0.5 - clamped) * shiftRange;

        /* -50% keeps the oversized cover image centered; translateY is scroll parallax only */
        backgroundLayer.style.transform = `translate3d(0, calc(-50% + ${translateY.toFixed(2)}px), 0)`;
      };

      const requestBackgroundUpdate = () => {
        if (scrollRafId) return;
        scrollRafId = requestAnimationFrame(updateBackgroundParallax);
      };

      requestBackgroundUpdate();
      window.addEventListener('scroll', requestBackgroundUpdate, { passive: true });
      window.addEventListener('resize', requestBackgroundUpdate);
    }
  };

  const boot = () => {
    document.querySelectorAll('[data-elevent-hero]').forEach(initHeroSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (!section || !section.matches('[data-elevent-hero]')) return;
    initHeroSection(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target;
    if (!section || !section.matches('[data-elevent-hero]')) return;
    destroyPathArrows(section);
    destroyGlowWind(section);
  });
})();
